import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FooterComponent } from '../../components/footer.component/footer.component';
import { NavComponent } from '../../components/nav.component/nav.component';
import { HttpService } from '../../services/http.service';

declare global {
  interface Window {
    turnstile: any;
  }
}

@Component({
  selector: 'app-contact.component',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    NavComponent,
    FooterComponent
  ],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.scss',
})
export class ContactComponent {
  contactForm: FormGroup;
  isSubmitting = false;
  submitSuccess = false;
  submitError = false;
  turnstileToken: string | null = null;

  contactMethods = [
    {
      icon: 'email',
      title: 'Email',
      value: 'contact@stml.app',
      link: 'mailto:contact@stml.app'
    },
    {
      icon: 'business',
      title: 'Business',
      value: 'JCC Development LLC',
      link: null
    }
  ];

  constructor(private fb: FormBuilder, private httpService: HttpService) {
    this.contactForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      company: [''],
      subject: ['', [Validators.required, Validators.minLength(5)]],
      message: ['', [Validators.required, Validators.minLength(20)]]
    });
  }

  ngOnInit(): void {
    this.loadTurnstile();
  }

  loadTurnstile(): void {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) {
        window.turnstile.render('#turnstile-widget', {
          sitekey: '0x4AAAAAACCuSgMKYY7jtpHQ',
          callback: (token: string) => {
            this.turnstileToken = token;
          },
        });
      }
    };
    document.head.appendChild(script);
  }

  onSubmit(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    if (!this.turnstileToken) {
      alert('Please complete the verification challenge');
      return;
    }

    this.isSubmitting = true;
    this.submitError = false;

    const { name, email, subject, message } = this.contactForm.value;

    this.httpService
      .post('/v1/contact', {
        name,
        email,
        subject,
        message,
        turnstileToken: this.turnstileToken,
      })
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.submitSuccess = true;
          this.contactForm.reset();
          this.turnstileToken = null;
          if (window.turnstile) {
            window.turnstile.reset();
          }
          setTimeout(() => {
            this.submitSuccess = false;
          }, 5000);
        },
        error: () => {
          this.isSubmitting = false;
          this.submitError = true;
          if (window.turnstile) {
            window.turnstile.reset();
          }
        },
      });
  }

  getErrorMessage(fieldName: string): string {
    const field = this.contactForm.get(fieldName);
    if (field?.hasError('required')) {
      return 'This field is required';
    }
    if (field?.hasError('email')) {
      return 'Please enter a valid email';
    }
    if (field?.hasError('minlength')) {
      const minLength = field.errors?.['minlength'].requiredLength;
      return `Minimum ${minLength} characters required`;
    }
    return '';
  }
}
